import { createAsyncThunk } from '@reduxjs/toolkit'

import { PRODUCT_PAGE, FULFILLED, CART } from '../../constants/constants'

import { AWAITING_SUBSTITUTIONS_ABORT, completeExecutionWithUpToDateBom } from './completeExecutionWithUpToDateBom'

import { getContent } from './apiRequests/getContent'
import { getLoadconfig } from './apiRequests/getLoadconfig'
import { type PartPaymentWidgetResponse, getPartPaymentWidget } from './apiRequests/getPartPaymentWidget'
import { postArticleinfolist } from './apiRequests/postArticleinfolist'
import { postLfcInitList } from './apiRequests/postLfcInitList'
import { postLfcConfigresultList } from './apiRequests/postLfcConfigresultList'
import { type DsaPricehelperResponse, postDsaPricehelper } from './apiRequests/postDsaPricehelper'
import { postSaveconfig } from './apiRequests/postSaveconfig'
import { postCartAddConfigurationSections } from './apiRequests/postCartAddConfigurationSections'
import { postEvent } from './apiRequests/postEvent'
import store, { type RootState } from '../store'
import { reportMissingArticles } from '../articleSubstitutions/articleSubstitutionsSlice'
import type * as DynamakerContract from '../../integration/DynamakerContract'
import { flattenBom } from '../../helpers/flattenBom'
import { applyDelayedDelivery } from '../../helpers/applyDelayedDelivery'

interface AsyncThunkConfig {
  state: RootState
}

// Applied to every bom on its way to pricing, YASS and the cart, so all three see the same choice
const withDelayedDeliveryChoice = (bom: DynamakerContract.BOM, state: RootState): DynamakerContract.BOM =>
  import.meta.env.VITE_SHOW_DELAYED_DELIVERY === 'true'
    ? applyDelayedDelivery(bom, state.ui.isDelayedDeliverySelected)
    : bom

export const fetchContent = createAsyncThunk<Awaited<ReturnType<typeof getContent>>, undefined, AsyncThunkConfig>(
  'thunks/fetchContent',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const { init, requests } = state
    const { channelId } = init
    const { apiConfigs } = requests

    const response = await getContent({
      apiConfigs,
      channelId,
    })

    return response
  }
)

export const fetchPartPaymentWidget = createAsyncThunk<PartPaymentWidgetResponse, undefined, AsyncThunkConfig>(
  'thunks/fetchPartPaymentWidget',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const { init, requests } = state
    const { channelId } = init
    const { apiConfigs } = requests

    const response = await getPartPaymentWidget({
      apiConfigs,
      channelId,
      onSuccess: (r) => r.data,
    })

    return response
  }
)

interface LoadConfigArgs {
  configId: string
  configHashKey: string
}

export const loadConfig = createAsyncThunk<Awaited<ReturnType<typeof getLoadconfig>>, LoadConfigArgs, AsyncThunkConfig>(
  'thunks/loadConfig',
  async ({ configId, configHashKey }, thunkAPI) => {
    const state = thunkAPI.getState()
    const { init, requests } = state
    const { applicationMode } = init
    const { apiConfigs } = requests

    const response = await getLoadconfig({
      apiConfigs,
      data: {
        configId,
        configHashKey,
        extrafields: applicationMode === PRODUCT_PAGE ? 'dmConfigurationSections,dmMetadata' : null,
      },
    })

    return response
  }
)

interface FetchDataForItemsArgs {
  items: Omit<DynamakerContract.Item, 'id' | 'item'>[]
  articlesInConfig: Set<string>
}

export const fetchDataForItems_aggregated = createAsyncThunk<any, FetchDataForItemsArgs, AsyncThunkConfig>(
  'thunks/fetchDataForItems_aggregated',
  async ({ items: mergedItems, articlesInConfig }, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      articleSubstitutions: { _knownMissingErpItemNumbers },
      init: { applicationMode },
      items: { erpItems: fetchedErpItems },
      requests,
    } = state
    const { apiConfigs } = requests

    const configurableItemsFromDm = mergedItems.filter((item) => item.props.configData)

    // Only fetch aggregated data for items that are not already in store (as erpItems), and never
    // for an article Scensum has already told us it doesn't recognise -- it never enters erpItems
    const articleinfolistData = mergedItems.flatMap((item) =>
      fetchedErpItems.some((fetchedErpItem) => fetchedErpItem.Sku === item.erpItemNumber) ||
      !item.erpItemNumber ||
      _knownMissingErpItemNumbers.includes(item.erpItemNumber)
        ? []
        : [{ Sku: item.erpItemNumber }]
    )

    const aggregatedResponse = await postArticleinfolist({
      apiConfigs,
      data: articleinfolistData,
      onSuccess: async (response) => {
        // Scensum returns Sku: null for article numbers it doesn't recognize, in an order we don't rely on
        const returnedSkus = new Set(response.data.map((erpItem) => erpItem.Sku))
        const missingErpItemNumbers: string[] = articleinfolistData.flatMap(({ Sku }) =>
          returnedSkus.has(Sku) ? [] : [Sku]
        )

        const erpItems = response.data.filter((erpItem) => erpItem.Sku !== null)

        const unsellableErpItemNumbers: string[] = []

        const postLfcConfigresultListData = {
          list: configurableItemsFromDm.flatMap((configurableItem) => {
            const matchingErpItem = erpItems.find((erpItem) => erpItem.Sku === configurableItem.erpItemNumber)
            if (!matchingErpItem) {
              console.error(
                'No article in response data from Scensum /articleinfolist matching configurableItem.erpItemNumber:',
                configurableItem.erpItemNumber
              )
              return []
            }

            // Scensum returns no PricingInfo for an article it cannot currently sell, so this covers unbuyable ones too
            if (!matchingErpItem.PricingInfo) {
              console.error('Article has no price', configurableItem.erpItemNumber)
              if (configurableItem.erpItemNumber) {
                unsellableErpItemNumbers.push(configurableItem.erpItemNumber)
              }
              return []
            }

            // TODO lägg tillbaka när bugg rättats i Scensum
            // if (!matchingErpItem.ContextId || !matchingErpItem.PricingInfo) {
            //   console.error(
            //     `Mallis article info is missing in response data from Scensum /articleinfolist for Sku ${matchingErpItem.Sku} -- ContextId is ${matchingErpItem.ContextId}, PricingInfo is ${matchingErpItem.PricingInfo}`
            //   );
            //   return [];
            // }
            // return {
            //   contextId: matchingErpItem.ContextId,
            return {
              contextId: matchingErpItem.Sku,
              namedConfigCodeRegexp: 'MOK-.+',
              pricingInfo: matchingErpItem.PricingInfo,
            }
          }),
        }

        // mergedItems also carries options the customer never picked, and only what is in the configuration can be substituted
        const missingErpItemNumbersInConfig = missingErpItemNumbers
          .concat(unsellableErpItemNumbers)
          .filter((Sku) => articlesInConfig.has(Sku))

        return await postLfcInitList({
          apiConfigs,
          data: postLfcConfigresultListData,
          onSuccess: (response) => {
            const { list, errorlist } = response.data

            if (errorlist.length) {
              // Not reported to DM: the shape of errorlist is undocumented, so we can't tell which articles failed
              console.error(
                'Errors in response data from LFC /multimallis/initlistbycontext:',
                JSON.stringify(response.data)
              )
            }

            const lfcItems = list.map(({ configuratorContextId, dimensions, constraints, presets }) => ({
              configuratorContextId,
              dimensions: dimensions.dimensions,
              constraintsRules: constraints.rules,
              presets,
            }))

            return {
              data: { erpItems, lfcItems, mergedItems, missingErpItemNumbers, missingErpItemNumbersInConfig },
            }
          },
        })
      },
    })

    // The product page has no DynaMaker to ask for substitutes, and nothing there would ever release the wait
    if (
      applicationMode !== PRODUCT_PAGE &&
      (aggregatedResponse.data.missingErpItemNumbers.length ||
        aggregatedResponse.data.missingErpItemNumbersInConfig.length)
    ) {
      store.dispatch(
        reportMissingArticles({
          knownMissing: aggregatedResponse.data.missingErpItemNumbers,
          toReport: aggregatedResponse.data.missingErpItemNumbersInConfig,
        })
      )
    }

    return aggregatedResponse.data
  },
  {
    condition: ({ items: mergedItems }, { getState }) => {
      const state = getState()
      const {
        articleSubstitutions: { _knownMissingErpItemNumbers },
        items: { erpItems },
        ui: { isFailedToLoadProjectModalOpen },
      } = state

      if (isFailedToLoadProjectModalOpen) return false

      const data = mergedItems.flatMap((item) =>
        // Do not fetch aggregated data for items if they are in store (as erpItems) or already known to be missing
        erpItems.some((erpItem) => erpItem.Sku === item.erpItemNumber) ||
        !item.erpItemNumber ||
        _knownMissingErpItemNumbers.includes(item.erpItemNumber)
          ? []
          : [{ Sku: item.erpItemNumber }]
      )

      return !!data.length
    },
  }
)

export const fetchPrices_aggregated = createAsyncThunk<
  any,
  Pick<DynamakerContract.ConfigResult, 'bom' | 'meta'> &
    Partial<Pick<DynamakerContract.ConfigResult, 'items' | 'props'>>,
  AsyncThunkConfig
>(
  'thunks/fetchPrices_aggregated',
  async (configResult, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      init: { content },
      items: { erpItems },
      requests,
      yass,
    } = state
    const { apiConfigs } = requests
    const { bom, meta, props, items } = configResult

    const promiseCreator = async (
      upToDateBom: DynamakerContract.BOM,
      bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
    ): Promise<{
      data: {
        bundle: DsaPricehelperResponse
        upToDateBom: typeof upToDateBom
        bomItemsWithUpdatedConfigData: typeof bomItemsWithUpdatedConfigData
      }
    }> =>
      postDsaPricehelper({
        apiConfigs,
        data: { bom: upToDateBom, configId: yass.configId, content, meta },
        onSuccess: (response) => {
          const bundle = response.data

          return {
            data: {
              bundle,
              upToDateBom,
              bomItemsWithUpdatedConfigData,
            },
          }
        },
      })

    try {
      const response = await completeExecutionWithUpToDateBom(
        withDelayedDeliveryChoice(bom, state),
        erpItems,
        apiConfigs,
        postLfcConfigresultList,
        promiseCreator
      )

      const { bundle, upToDateBom, bomItemsWithUpdatedConfigData } = response.data

      return {
        bundle,
        configResult: {
          bom: upToDateBom,
          meta,
          props,
          items,
        },
        bomItemsWithUpdatedConfigData,
      }
    } catch (error) {
      return thunkAPI.rejectWithValue({
        configResult,
        // The wait can start mid-flight, after condition below has already let the call through
        _abortedAwaitingSubstitutions: error instanceof Error && error.message === AWAITING_SUBSTITUTIONS_ABORT,
      })
    }
  },
  {
    // Pricing a bom that DM is about to substitute articles in would only be thrown away
    condition: (_, { getState }) => !getState().articleSubstitutions.isAwaitingArticleSubstitutions,
  }
)

interface AddToCartArgs {
  config: DynamakerContract.ProjectData['config']
  bom: DynamakerContract.ProjectData['bom']
  meta: DynamakerContract.ProjectData['meta']

  props?: DynamakerContract.ProjectData['props'] // do we need these at all?
  items?: DynamakerContract.ProjectData['items'] // do we need these at all?

  internalSaveUuidv4: string
}

export const addToCart_aggregated = createAsyncThunk<any, AddToCartArgs, AsyncThunkConfig>(
  'thunks/addToCart_aggregated',
  async ({ config, bom, meta, props, items, internalSaveUuidv4 }, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      bundle,
      customer,
      init: { content },
      items: { erpItems },
      requests,
      yass,
    } = state
    const { apiConfigs, requestStatus } = requests

    if (requestStatus.requestStatus_fetchPrices_aggregated !== FULFILLED) {
      console.error('thunks/addToCart_aggregated was dispatched when fetchPrices_aggregated was not fulfilled')
    }

    const promiseCreator = async (
      upToDateBom: DynamakerContract.BOM,
      bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
    ): Promise<{
      data: {
        _configHashKey: string
        _configId: string
        bomItemsWithUpdatedConfigData: typeof bomItemsWithUpdatedConfigData
        upToDateBom: typeof upToDateBom
      }
    }> =>
      await postSaveconfig({
        apiConfigs,
        data: {
          config,
          bom: flattenBom(upToDateBom),
          configurationSections: upToDateBom,
          meta,
          bundle,
          customer,
          yass,
          type: CART,
        },
        onSuccess: async (response) => {
          const { yassConfigId: configId, yassConfigHashKey: configHashKey } = response.data

          return await postCartAddConfigurationSections({
            apiConfigs,
            data: {
              bom: upToDateBom,
              configId,
              content,
              meta,
            },
            onSuccess: () => {
              return {
                data: {
                  _configId: configId,
                  _configHashKey: configHashKey,
                  upToDateBom,
                  bomItemsWithUpdatedConfigData,
                },
              }
            },
          })
        },
      })

    const response = await completeExecutionWithUpToDateBom(
      withDelayedDeliveryChoice(bom, state),
      erpItems,
      apiConfigs,
      postLfcConfigresultList,
      promiseCreator
    )

    const { _configId, _configHashKey, upToDateBom, bomItemsWithUpdatedConfigData } = response.data

    return {
      _internalSaveUuidv4: internalSaveUuidv4,
      _configId,
      _configHashKey,
      configResult: {
        bom: upToDateBom,
        meta,
        props,
        items,
      },
      bomItemsWithUpdatedConfigData,
    }
  }
)

interface SubmitFormArgs extends Pick<DynamakerContract.ProjectData, 'config' | 'bom' | 'meta' | 'props' | 'items'> {
  type: string
  internalSaveUuidv4: string
}

export const submitForm_aggregated = createAsyncThunk<any, SubmitFormArgs, AsyncThunkConfig>(
  'thunks/submitForm_aggregated',
  async ({ config, bom, meta, props, items, internalSaveUuidv4, type }, thunkAPI) => {
    const state = thunkAPI.getState()
    const {
      bundle,
      customer,
      items: { erpItems },
      requests,
      yass,
    } = state
    const { apiConfigs, requestStatus } = requests

    if (requestStatus.requestStatus_fetchPrices_aggregated !== FULFILLED) {
      console.error('thunks/submitForm_aggregated was dispatched when fetchPrices_aggregated was not fulfilled')
    }

    const promiseCreator = async (
      upToDateBom: DynamakerContract.BOM,
      bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
    ): Promise<{
      data: {
        _configHashKey: string
        _configId: string
        upToDateBom: typeof upToDateBom
        bomItemsWithUpdatedConfigData: typeof bomItemsWithUpdatedConfigData
      }
    }> => {
      return await postSaveconfig({
        apiConfigs,
        data: {
          config,
          bom: flattenBom(upToDateBom),
          configurationSections: upToDateBom,
          meta,
          bundle,
          customer,
          yass,
          type,
        },
        onSuccess: async (response) => {
          const { yassConfigId: configId, yassConfigHashKey: configHashKey } = response.data

          return await postEvent({
            apiConfigs,
            data: { customer, yass: { configId, configHashKey }, type },
            onSuccess: () => {
              return {
                data: {
                  _configId: configId,
                  _configHashKey: configHashKey,
                  upToDateBom,
                  bomItemsWithUpdatedConfigData,
                },
              }
            },
          })
        },
      })
    }

    const response = await completeExecutionWithUpToDateBom(
      withDelayedDeliveryChoice(bom, state),
      erpItems,
      apiConfigs,
      postLfcConfigresultList,
      promiseCreator
    )

    const { _configId, _configHashKey, upToDateBom, bomItemsWithUpdatedConfigData } = response.data

    return {
      _internalSaveUuidv4: internalSaveUuidv4,
      _configId,
      _configHashKey,
      configResult: {
        bom: upToDateBom,
        meta,
        props,
        items,
      },
      bomItemsWithUpdatedConfigData,
    }
  }
)

interface FetchSubstitutedArticleTitlesArgs {
  erpItemNumbers: string[]
}

// Kept apart from erpItems: substituted articles are no longer part of the configuration and must not affect pricing or rendering
export const fetchSubstitutedArticleTitles = createAsyncThunk<
  Record<string, string>,
  FetchSubstitutedArticleTitlesArgs,
  AsyncThunkConfig
>('thunks/fetchSubstitutedArticleTitles', async ({ erpItemNumbers }, thunkAPI) => {
  const state = thunkAPI.getState()
  const { apiConfigs } = state.requests

  return await postArticleinfolist({
    apiConfigs,
    data: erpItemNumbers.map((Sku) => ({ Sku })),
    onSuccess: (response) =>
      Object.fromEntries(
        response.data.flatMap((erpItem) => (erpItem.Sku && erpItem.Title ? [[erpItem.Sku, erpItem.Title]] : []))
      ),
  })
})
